/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {CommonModule} from '@angular/common';
import {IxModule, ModalService} from '@siemens/ix-angular';
import {
  BatteryAncestor,
  ListBatteryObject,
} from '../../../../../models/ListBattery';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {AgGridModule} from 'ag-grid-angular';
import {
  ColDef,
  GridOptions,
  GridReadyEvent,
  ICellRendererParams,
  IDatasource,
  IGetRowsParams,
  ITooltipParams,
  RowClickedEvent,
} from 'ag-grid-community';
import {getIxTheme} from '@siemens/ix-aggrid';
import * as agGrid from 'ag-grid-community';
import {AppSettings} from '../../../../../models/Constants';
import {getSchemaVersionLabel} from '../../schema-version-label.pipe';
import {PassportService} from '../../../../../services/passport.service';
import {SchemaListItemDTO} from '../../../../../models/Schema';
import {Router, RouterModule} from '@angular/router';
import {MasterChildStringEnum} from '../../../../../models/Passport';
import {ModalCreateChildrenComponent} from '../../modal-create-children/modal-create-children.component';
import {NgxPermissionsModule} from 'ngx-permissions';
import {FeatureFlaggingService} from '../../../../../services/feature-flagging.service';
import {FeatureFlag} from '../../../../../models/FeatureFlag';
import {QRCodeComponent} from 'angularx-qrcode';
import {HierarchyGraphComponent} from '../../../../shared/hierarchy-graph/hierarchy-graph.component';

@Component({
  selector: 'app-pass-list-details',
  imports: [
    CommonModule,
    IxModule,
    TranslateModule,
    AgGridModule,
    NgxPermissionsModule,
    HierarchyGraphComponent,
    QRCodeComponent,
    RouterModule,
  ],
  templateUrl: './pass-list-details.component.html',
  styleUrl: './pass-list-details.component.scss',
})
export class PassListDetailsComponent implements OnChanges, OnInit {
  private readonly passportService = inject(PassportService);
  private readonly translateService = inject(TranslateService);
  private readonly featureFlaggingService = inject(FeatureFlaggingService);
  private readonly modalService = inject(ModalService);
  private readonly router = inject(Router);

  @Input() passport!: ListBatteryObject;
  @Input() schemas!: SchemaListItemDTO[];
  @Output() eventClick = new EventEmitter<void>();
  @Output() removeClick = new EventEmitter<void>();
  numberOfTotalPassports = 0;

  loading = false;
  keyValueList: {key: string; value?: string; type: string}[] = [];
  columnDefs: ColDef[] = [];

  featureFlag?: FeatureFlag;
  selectedTabIndex = 0;
  hierarchyAncestorData?: BatteryAncestor['data'];
  hierarchyDescendantData: ListBatteryObject[] = [];

  dataGridApi: GridReadyEvent['api'];
  hierarchyGridApi: GridReadyEvent['api'];
  gridOptions: GridOptions = {
    theme: getIxTheme(agGrid),
    rowModelType: 'infinite',
    cacheBlockSize: AppSettings.BATTERY_LIST_PAGE_SIZE,
    maxBlocksInCache: 2,
    suppressRowClickSelection: true,
    rowSelection: 'single',
    onRowClicked: this.onRowClicked.bind(this),
    pagination: false,
    enableCellTextSelection: true,
    suppressMovableColumns: true,
    suppressRowTransform: true,
    context: {
      componentParent: this,
    },
  };
  protected readonly MasterChildFlag = MasterChildStringEnum;

  onViewClick() {
    this.router.navigate(['/app/view-passport/', this.passport.id]);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['passport']?.currentValue) {
      this.loading = true;
      this.selectedTabIndex = 0;
      this.createDetailsList();
      this.createColumnDefs();
      this.loadMasterChildData().then(() => (this.loading = false));
    }
  }

  ngOnInit() {
    this.translateService.onLangChange.subscribe(() => {
      if (this.dataGridApi) {
        this.dataGridApi.purgeInfiniteCache();
        this.dataGridApi.refreshHeader();
      }
    });
    this.featureFlaggingService.load().then((features) => {
      this.featureFlag = features;
    });
  }

  createDetailsList() {
    const schema = this.schemas?.find(
      (s) => s.id === this.passport?.metadata.schemaId,
    );
    this.keyValueList = [
      {
        key: 'battery_pass.list.id',
        value: this.passport?.id,
        type: 'text',
      },
      {
        key: 'battery_pass.list.type',
        value: this.passport?.metadata.masterChildFlag,
        type: 'type',
      },
      {
        key: 'battery_pass.list.product',
        value: schema
          ? `${schema.name} (${getSchemaVersionLabel(schema, this.schemas)})`
          : this.passport?.metadata.schemaId,
        type: 'text',
      },
      {
        key: 'battery_pass.list.schema_version',
        value: schema?.version ? `v${schema.version}` : undefined,
        type: 'text',
      },
    ];
    if (schema?.type === 'battery' && schema?.standardVersion) {
      this.keyValueList.push({
        key: 'battery_pass.list.longlist_version',
        value: `v${schema.standardVersion}`,
        type: 'text',
      });
    }
    this.keyValueList.push(
      {
        key: 'battery_pass.list.created_at',
        value: new Date(this.passport?.metadata.createdAt).toLocaleString(),
        type: 'text',
      },
      {
        key: 'battery_pass.list.modified_at',
        value: new Date(this.passport?.metadata.updatedAt).toLocaleString(),
        type: 'text',
      },
      {
        key: 'battery_pass.list.status',
        value: this.passport?.metadata.status.status,
        type: 'status',
      },
    );
    if (this.passport?.metadata.name) {
      this.keyValueList.unshift({
        key: 'battery_pass.list.name',
        value: this.passport?.metadata.name,
        type: 'text',
      });
    }
  }

  createColumnDefs() {
    this.columnDefs = [
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.id_name'),
        maxWidth: 280,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject | undefined;
          return passport?.metadata?.name || passport?.id;
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return passport?.metadata?.name || passport?.id;
          } else {
            return '';
          }
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.modified_at'),
        maxWidth: 155,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return new Date(passport?.metadata?.updatedAt).toLocaleString();
          } else {
            return '';
          }
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            return new Date(passport?.metadata?.updatedAt).toLocaleString();
          } else {
            return '';
          }
        },
      },
      {
        headerValueGetter: () =>
          this.translateService.instant('battery_pass.list.status'),
        maxWidth: 110,
        cellRenderer: (params: ICellRendererParams) => {
          const passport = params.data as ListBatteryObject;
          if (!passport) {
            return '';
          }
          const status = passport?.metadata?.status?.status;
          if (!status) {
            return '';
          }

          const success = status === 'draft' ? 'critical' : 'success';
          const translatedStatus = this.translateService.instant(
            `battery_pass.list.${status}`,
          );

          return `<ix-pill variant="${success}">${translatedStatus}</ix-pill>`;
        },
        tooltipValueGetter: (params: ITooltipParams) => {
          const passport = params.data as ListBatteryObject;
          if (passport !== undefined) {
            const status = passport?.metadata?.status?.status;
            if (!status) {
              return '';
            }
            return this.translateService.instant(`battery_pass.list.${status}`);
          } else {
            return '';
          }
        },
      },
    ];
  }

  async onDataGridReady(params: GridReadyEvent) {
    this.loading = true;
    this.dataGridApi = params.api;
    await this.loadMasterChildData();
    this.loading = false;
  }

  async onHierarchyGridReady(params: GridReadyEvent) {
    this.loading = true;
    this.hierarchyGridApi = params.api;
    await this.loadHierarchyData();
    this.loading = false;
  }

  async loadMasterChildData() {
    let datasource: IDatasource | undefined;
    if (
      this.passport.metadata.masterChildFlag === MasterChildStringEnum.MASTER
    ) {
      datasource = await this.loadChildren(this.passport.id);
    } else {
      datasource = await this.loadMasters(
        this.passport.metadata.masterPassportId,
      );
    }
    if (this.dataGridApi && datasource) {
      this.dataGridApi.setGridOption('datasource', datasource);
    }
  }

  async loadHierarchyData() {
    const datasource = await this.loadHierarchy(this.passport.id);
    if (this.hierarchyGridApi && datasource) {
      this.hierarchyGridApi.setGridOption('datasource', datasource);
    }
  }

  /**
   * Loads the complete hierarchy (ancestors and descendants) for a given passport.
   *
   * This method fetches both the ancestor passport and all descendant passports,
   * then creates an AG Grid datasource that combines them for display in the hierarchy view.
   * The ancestor is placed at the top, followed by all descendants.
   *
   * @param {string} passportId - The ID of the passport to load the hierarchy for.
   * @returns {Promise<IDatasource>} A promise that resolves to an AG Grid datasource
   *                                  containing the getRows callback for infinite scrolling.
   *
   * @remarks
   * - Sets `this.loading` to true at the start and false when data is loaded
   * - Updates `this.hierarchyAncestorData` with the ancestor passport data
   * - Updates `this.hierarchyDescendantData` with the array of descendant passports
   * - Calculates `this.numberOfTotalPassports` as the sum of ancestors and descendants
   * - The returned datasource provides rows with ancestor first, then descendants
   *
   */
  async loadHierarchy(passportId: string): Promise<IDatasource> {
    this.loading = true;
    this.hierarchyAncestorData =
      (await this.passportService.loadPassportAncestor(passportId)).data ||
      undefined;
    const descendantsResponse =
      await this.passportService.loadPassportDescendants(passportId);
    this.hierarchyDescendantData = descendantsResponse.data;
    this.numberOfTotalPassports =
      (this.hierarchyAncestorData ? 1 : 0) +
      (descendantsResponse.meta?.page.totalElements ?? 0);
    return {
      getRows: (params: IGetRowsParams) => {
        const result = [];
        if (
          this.hierarchyAncestorData &&
          Object.keys(this.hierarchyAncestorData).length > 0
        ) {
          result.push(
            this.hierarchyAncestorData as unknown as ListBatteryObject,
          );
        }
        params.successCallback(
          result.concat(this.hierarchyDescendantData),
          this.numberOfTotalPassports,
        );
        this.loading = false;
      },
    };
  }

  async loadChildren(masterId: string): Promise<IDatasource> {
    return {
      getRows: async (params: IGetRowsParams) => {
        this.loading = true;
        const result = await this.passportService.loadList({
          limit: params.endRow - params.startRow,
          offset: params.startRow,
          masterPassportId: masterId,
        });
        this.numberOfTotalPassports = result.meta?.page.totalElements ?? 0;
        params.successCallback(result.data, result.meta?.page.totalElements);
        this.loading = false;
      },
    };
  }

  async loadMasters(masterId: string): Promise<IDatasource> {
    return {
      getRows: async (params: IGetRowsParams) => {
        this.loading = true;
        this.numberOfTotalPassports = 0;
        if (masterId) {
          const result = await this.passportService.loadList({
            limit: params.endRow - params.startRow,
            offset: params.startRow,
            passportId: masterId,
          });
          this.numberOfTotalPassports = result.meta?.page.totalElements ?? 0;
          params.successCallback(result.data, this.numberOfTotalPassports);
        } else {
          params.successCallback([], 0);
        }
        this.loading = false;
      },
    };
  }

  getProduct(schemaId: string) {
    if (!this.schemas) {
      return schemaId;
    }
    const schema = this.schemas.find(
      (schemaItem) => schemaItem.id === schemaId,
    );
    return schema
      ? `${schema.name} (${getSchemaVersionLabel(schema, this.schemas)})`
      : schemaId;
  }

  async onCreateChildClick(): Promise<void> {
    const masterPassportId = this.passport.id;
    const instance = await this.modalService.open({
      size: '480',
      content: ModalCreateChildrenComponent,
      data: {id: masterPassportId},
      centered: true,
      closeOnBackdropClick: true,
    });

    instance.onClose.on(async (finishText: string) => {
      if (finishText === 'finish') {
        this.dataGridApi?.purgeInfiniteCache();
        this.eventClick.emit();
      }
    });
  }

  async onRowClicked(event: RowClickedEvent): Promise<void> {
    if (!event.data) {
      return;
    }
    await this.router.navigate(['/app/view-passport/', event.data.id]);
  }
}
